import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Card,
  Row,
  Col,
  Typography,
  Tabs,
  List,
  Button,
  Tag,
  Empty,
  Space,
} from "antd";
import {
  BookOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getSessionId, getUserDetails, isAdminUser } from "../../../services/authService";
import { resolveApiUrl } from "../../../assets/GlobalVariables";
import { useLocation, useNavigate } from "react-router-dom";
import "./Academy.scss";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const Academy = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("videos");
  const user = getUserDetails();
  const isAdmin = isAdminUser();
  const isDev = Boolean(import.meta.env.DEV);
  const targetPath = "/p/dashboard/admin/academy";
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videosError, setVideosError] = useState("");
  const [markingVideoIds, setMarkingVideoIds] = useState({});

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log("[ADMIN_DETECTION_DEBUG][Academy]", {
      user,
      role: user?.role ?? user?.agentRole ?? null,
      usertype: user?.usertype ?? user?.userType ?? null,
      is_admin: user?.is_admin ?? user?.isAdmin ?? null,
      email: user?.email ?? null,
      isAdminUserResult: isAdmin,
    });
  }, [isAdmin, user]);

  useEffect(() => {
    if (!isDev) return;
    console.log("[ACADEMY_UPLOAD_ROUTE_MATCH]", {
      pathname: location.pathname,
      isAcademy: location.pathname === "/p/dashboard/academy",
      isAdminAcademy: location.pathname === targetPath,
    });
  }, [isDev, location.pathname]);

  const handleManageVideosClick = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (isDev) {
      console.log("[ACADEMY_UPLOAD_BUTTON_CLICK]", {
        from: "/p/dashboard/academy",
        isAdmin,
      });
      console.log("[ACADEMY_UPLOAD_BUTTON_TARGET]", { targetPath });
      console.log("[ACADEMY_UPLOAD_NAVIGATE_START]", {
        pathname: window.location.pathname,
        targetPath,
      });
    }

    navigate(targetPath);

    window.setTimeout(() => {
      if (isDev) {
        console.log("[ACADEMY_UPLOAD_NAVIGATE_DONE]", {
          pathname: window.location.pathname,
          targetPath,
        });
      }
      if (window.location.pathname !== targetPath) {
        window.location.assign(targetPath);
      }
    }, 50);
  };

  const courses = [];
  const documents = [];
  const achievements = [];

  const fetchVideos = useCallback(async () => {
    setVideosLoading(true);
    setVideosError("");
    try {
      const sid = await getSessionId();
      const headers = {};
      if (sid) {
        headers.Authorization = `Bearer ${sid}`;
        headers["X-Session-ID"] = sid;
      }
      const { data } = await axios.get(resolveApiUrl("/api/academy/videos"), {
        withCredentials: true,
        headers,
      });
      const rows = Array.isArray(data?.videos) ? data.videos : [];
      setVideos(rows);
      if (isDev) {
        console.log("[ACADEMY_VIDEOS_FETCH]", {
          isAdmin,
          videosCount: rows.length,
        });
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "אירעה שגיאה בטעינת סרטוני ההדרכה";
      setVideosError(message);
      setVideos([]);
    } finally {
      setVideosLoading(false);
    }
  }, [isAdmin, isDev]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const markVideoAsWatched = async (videoId) => {
    if (!videoId || markingVideoIds[videoId]) return;
    setMarkingVideoIds((prev) => ({ ...prev, [videoId]: true }));
    try {
      const sid = await getSessionId();
      const headers = {};
      if (sid) {
        headers.Authorization = `Bearer ${sid}`;
        headers["X-Session-ID"] = sid;
      }
      await axios.post(
        resolveApiUrl(`/api/academy/videos/${encodeURIComponent(videoId)}/view`),
        sid ? { sessionID: sid } : {},
        { withCredentials: true, headers }
      );
      setVideos((prev) =>
        prev.map((v) => (v.id === videoId ? { ...v, watched: true } : v))
      );
    } catch (err) {
      if (isDev) {
        console.log("[ACADEMY_VIDEO_VIEW_MARKED] failed", {
          videoId,
          error: err?.message || null,
        });
      }
    } finally {
      setMarkingVideoIds((prev) => ({ ...prev, [videoId]: false }));
    }
  };

  const renderCourseCard = (course) => (
    <Card
      key={course.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button type="link" icon={<PlayCircleOutlined />}>
          התחל קורס
        </Button>,
      ]}
    >
      <Card.Meta
        title={
          <Space>
            <BookOutlined />
            {course.title}
          </Space>
        }
        description={
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Text type="secondary">{course.description}</Text>
            <Space>
              <Tag color="blue">{course.category}</Tag>
              <Tag icon={<ClockCircleOutlined />}>{course.duration}</Tag>
              {course.completed && <Tag color="green">הושלם</Tag>}
            </Space>
          </Space>
        }
      />
    </Card>
  );

  const renderVideoCard = (video) => (
    <Card
      key={video.id}
      hoverable
      className="academy-video-card"
      cover={
        video.youtube_video_id ? (
          <div className="academy-video-frame-wrapper">
            <iframe
              src={`https://www.youtube.com/embed/${encodeURIComponent(video.youtube_video_id)}`}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        ) : (
          <div className="academy-video-placeholder">
            <PlayCircleOutlined style={{ fontSize: 64, color: "white" }} />
          </div>
        )
      }
    >
      <Card.Meta
        title={
          <Space>
            <span>{video.title}</span>
            <Tag color={video.watched ? "green" : "default"}>
              {video.watched ? "נצפה" : "לא נצפה"}
            </Tag>
          </Space>
        }
        description={
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Text type="secondary">{video.description}</Text>
            <Space wrap>
              {video.category ? <Tag color="blue">{video.category}</Tag> : null}
              <Button
                type={video.watched ? "default" : "primary"}
                onClick={() => markVideoAsWatched(video.id)}
                loading={Boolean(markingVideoIds[video.id])}
                disabled={Boolean(video.watched)}
              >
                {video.watched ? "סומן כצפוי" : "סמן כצפיתי"}
              </Button>
            </Space>
          </Space>
        }
      />
    </Card>
  );

  return (
    <div className="academy-page">
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Card>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <Title level={2} style={{ marginBottom: 0 }}>
                <TrophyOutlined /> אקדמיה והדרכות
              </Title>
              {isAdmin ? (
                <Button
                  type="primary"
                  htmlType="button"
                  icon={<SettingOutlined />}
                  onMouseDown={() => {
                    if (!isDev) return;
                    console.log("[ACADEMY_MANAGE_MOUSEDOWN]");
                  }}
                  onClick={(e) => {
                    if (isDev) {
                      const btn = e.currentTarget?.getBoundingClientRect?.();
                      if (btn) {
                        const x = Math.round(btn.left + btn.width / 2);
                        const y = Math.round(btn.top + btn.height / 2);
                        const topEl = document.elementFromPoint(x, y);
                        console.log("[ACADEMY_UPLOAD_ELEMENT_FROM_POINT]", {
                          x,
                          y,
                          topTag: topEl?.tagName || null,
                          topClass: topEl?.className || null,
                        });
                      }
                    }
                    handleManageVideosClick(e);
                  }}
                >
                  ניהול והעלאת סרטונים
                </Button>
              ) : null}
            </div>
            <Text type="secondary">
              קורסים, סרטונים ומדריכים לשיפור הידע והמיומנויות שלך
            </Text>
          </Card>
        </Col>

        <Col xs={24}>
          <Card>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              size="large"
              type="card"
            >
              <TabPane
                tab={
                  <span>
                    <PlayCircleOutlined />
                    סרטוני הדרכה
                  </span>
                }
                key="videos"
              >
                {videosError ? (
                  <Alert
                    type="error"
                    showIcon
                    message="שגיאה בטעינת סרטונים"
                    description={videosError}
                    action={
                      <Button size="small" onClick={fetchVideos}>
                        נסה שוב
                      </Button>
                    }
                  />
                ) : null}
                {videosLoading ? (
                  <div style={{ padding: 24 }}>
                    <Text type="secondary">טוען סרטונים...</Text>
                  </div>
                ) : null}
                {!videosLoading && !videosError && videos.length === 0 ? (
                  <Empty
                    description="אין סרטונים זמינים כרגע"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : null}
                {!videosLoading && !videosError && videos.length > 0 ? (
                  <Row gutter={[16, 16]}>
                    {videos.map((video) => (
                      <Col xs={24} sm={12} lg={8} key={video.id}>
                        {renderVideoCard(video)}
                      </Col>
                    ))}
                  </Row>
                ) : null}
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <BookOutlined />
                    קורסים
                  </span>
                }
                key="courses"
              >
                {courses.length === 0 ? (
                  <Empty
                    description="אין קורסים זמינים כרגע"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  <Row gutter={[16, 16]}>
                    {courses.map((course) => (
                      <Col xs={24} sm={12} lg={8} key={course.id}>
                        {renderCourseCard(course)}
                      </Col>
                    ))}
                  </Row>
                )}
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <FileTextOutlined />
                    מסמכים ומדריכים
                  </span>
                }
                key="documents"
              >
                {documents.length === 0 ? (
                  <Empty
                    description="אין מסמכים זמינים כרגע"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  <List
                    dataSource={documents}
                    renderItem={(doc) => (
                      <List.Item
                        actions={[
                          <Button type="link" icon={<FileTextOutlined />}>
                            הורד
                          </Button>,
                        ]}
                      >
                        <List.Item.Meta
                          title={doc.title}
                          description={doc.description}
                        />
                      </List.Item>
                    )}
                  />
                )}
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <TrophyOutlined />
                    הישגים
                  </span>
                }
                key="achievements"
              >
                {achievements.length === 0 ? (
                  <Empty
                    description="אין הישגים זמינים כרגע"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  <Row gutter={[16, 16]}>
                    {achievements.map((achievement) => (
                      <Col xs={24} sm={12} lg={8} key={achievement.id}>
                        <Card>
                          <Space direction="vertical" align="center" style={{ width: "100%" }}>
                            <TrophyOutlined style={{ fontSize: 48, color: "#faad14" }} />
                            <Title level={4}>{achievement.title}</Title>
                            <Text type="secondary">{achievement.description}</Text>
                            <Tag color="gold">{achievement.date}</Tag>
                          </Space>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Academy;
